/**
 * <CMS1500FormPreview /> — embed + download for the filled CMS-1500 PDF.
 *
 * Two modes via the ``mode`` prop:
 *   - mode="claim"        → fetches /api/claims/{id}/cms1500-pdf
 *   - mode="note-preview" → fetches /api/claims/preview-from-note/{id}/cms1500-pdf
 *
 * Embeds the PDF in an <iframe> (browsers' built-in PDF.js viewer
 * handles zoom, scroll, print).  A "Download PDF" button on top
 * issues the same bytes for offline / mailing.
 *
 * Race-guarded by ``useClaimPDF`` so the caller can pass changing
 * ids without worrying about stale results.
 */
import { useEffect } from 'react';
import { Button } from '../ui/button';
import { Download, AlertCircle, Loader2 } from 'lucide-react';
import { useClaimPDF } from '../../hooks/useClaimPDF';

export const CMS1500FormPreview = ({
  mode = 'claim',
  claimId,
  noteId,
  filenameHint,
  height = '85vh',
}) => {
  const { fetchClaimPdf, fetchNotePreviewPdf, pdfUrl, loading, error, downloadAs, reset } = useClaimPDF();

  // Refetch whenever the target id changes; reset on unmount.
  useEffect(() => {
    if (mode === 'claim' && claimId) {
      fetchClaimPdf(claimId);
    } else if (mode === 'note-preview' && noteId) {
      fetchNotePreviewPdf(noteId);
    }
    return () => reset();
  }, [mode, claimId, noteId]);  // eslint-disable-line

  if (loading) {
    return (
      <div data-testid="cms1500-pdf-loading" className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        <span className="ml-2 text-sm text-gray-600">Generating CMS-1500…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="cms1500-pdf-error" className="border border-red-200 bg-red-50 rounded p-4 flex items-start gap-2">
        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-medium text-red-800">Could not generate PDF</p>
          <p className="text-red-700 mt-0.5">{error}</p>
        </div>
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div data-testid="cms1500-pdf-empty" className="text-sm text-gray-500 p-4">
        No PDF loaded.
      </div>
    );
  }

  return (
    <div data-testid="cms1500-pdf-preview" className="space-y-2">
      <div className="flex justify-end">
        <Button
          data-testid="cms1500-pdf-download"
          variant="outline"
          size="sm"
          onClick={() => downloadAs(filenameHint)}
          className="gap-1.5"
        >
          <Download className="h-3.5 w-3.5" />
          Download PDF
        </Button>
      </div>
      <iframe
        data-testid="cms1500-pdf-iframe"
        src={pdfUrl}
        title="CMS-1500 (02-12) Filled Form"
        style={{ width: '100%', height, border: '1px solid #d1d5db', borderRadius: 6 }}
      />
    </div>
  );
};

export default CMS1500FormPreview;
